import { useState } from 'react';
import { updateProgramInFirestore, deleteProgramFromFirestore } from '../../utilityFunctions/firestoreUtilities';
import { cleanText } from '../../utilityFunctions/uxUtilities';
import StateOptions from './StateOptions';
import {NteeDropdown} from "./NteeDropdown";
import nteeMajorNames from '../../assets/ntee_major_names.json';

const ManageProgramModal = ({ 
        currentProgram,
        currentClientOrganization,
        fetchProgramsForOrganization,
        setCurrentProgram,  
        setShowManageProgramModal,
        setShowCreateProgramModal
    }) => {
    
    // State for the form values
    const [formValues, setFormValues] = useState({
        programName: currentProgram.programName,        
        primaryState: currentProgram.primaryState,
        nteeFull: currentProgram.nteeFull,
        additionalDetails: currentProgram.additionalDetails,        
    });
    
    const handleDeleteProgram = async (programId) => {
        const confirmed = window.confirm("Are you sure you want to delete this program? This will delete all saved opportunities for this program.");
        if (!confirmed) return;

        try {
            await deleteProgramFromFirestore(programId);
            const refreshedPrograms = await fetchProgramsForOrganization(currentClientOrganization.id);
            setCurrentProgram(refreshedPrograms[0]);
            setShowManageProgramModal(false);
        } catch (error) {
            console.error("Error deleting program: ", error);
        }

    }

    const getNteeMajorDescription = (nteeMajor) => {
        const majorEntry = nteeMajorNames.find(major => major.ntee_major === nteeMajor);
        return majorEntry ? majorEntry.description : nteeMajor;
    };

    // Submit form to create a program
    const handleSubmit = async () => {
        // Check if all fields are filled
        const { programName, primaryState, nteeFull, additionalDetails } = formValues;
        if (!programName || !primaryState || !nteeFull || !additionalDetails) {
            alert("Please fill in all fields before submitting.");
            return; // Prevent submission if any field is empty
        }
        
        const cleanedProgramName = cleanText(programName);
        const cleanedPrimaryState = cleanText(primaryState);
        const cleanedNteeFull = cleanText(nteeFull);
        const cleanedAdditionalDetails = cleanText(additionalDetails);

        await updateProgramInFirestore(currentProgram.id, cleanedProgramName, cleanedPrimaryState, cleanedNteeFull, cleanedAdditionalDetails);

        const refreshedPrograms = await fetchProgramsForOrganization(currentClientOrganization.id);
        
        const updatedProgram = refreshedPrograms.find(program => program.id === currentProgram.id);
        setCurrentProgram(updatedProgram);
        setShowManageProgramModal(false);
    };
    
    // Handle form input changes 
    const handleChange = (field, value) => {
        setFormValues(prevValues => ({
            ...prevValues,
            [field]: value
        }));
    };

    return (
        <div 
            className="modal-overlay" 
            style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}
            onClick={() => setShowManageProgramModal(false)}
        >
            <div 
                className="modal p-4 sm:p-6 lg:p-8 overflow-auto max-h-full mx-4 sm:mx-0" 
                style={{ 
                    cursor: 'default', 
                    width: '95vw',
                    maxWidth: '600px',
                    minHeight: 'auto',
                    maxHeight: '90vh',
                    backgroundColor: 'white', 
                    borderRadius: '10px'
                }}
                onClick={(e) => e.stopPropagation()} // Prevent click event from propagating to the overlay
            >
                {/* <p className="text-3xl mt-10 font-bold text-left">{existingProgram ? 'Update Program' : 'Program Details'}</p>                  */}
                <p className="text-2xl sm:text-3xl mt-6 sm:mt-10 font-bold text-left">Manage Program</p>                 
                <p className="text-sm mt-2 text-left">Daisy will learn about your program from the following information. <a className="text-indigo-500" href="https://www.notion.so/GrantMagic-Documentation-17546353e32d8076a042c52f7b792c8d?pvs=4#17b46353e32d80408f97ec77e2e5f4ba" target="_blank" rel="noopener noreferrer">Learn More</a></p>
                
                <div className='text-left mt-6 sm:mt-10'>
                    
                    <div className="mb-4">
                        <label className="block font-bold mb-2 text-sm sm:text-base">Program Name <span className="text-red-500">*</span></label>
                        <input 
                            className="w-full p-2 sm:p-3 border rounded text-sm sm:text-base" 
                            value={formValues.programName}
                            onChange={(e) => handleChange('programName', e.target.value)}
                            required
                        />
                    </div>
                   
                    <NteeDropdown 
                        onChange={(value) => {
                            console.log("NTEE Code: ", value);
                            handleChange('nteeFull', value)
                        }} 
                        code={formValues.nteeFull}
                    >
                        <label className="block font-bold mb-1 text-sm sm:text-base">NTEE Code (Full) <span className="text-red-500">*</span></label>
                        <p className="text-xs sm:text-sm mb-2 text-left italic text-gray-500">
                            To find your NTEE code, please visit the <a className="text-indigo-500" href="https://urbaninstitute.github.io/nccs-legacy/ntee/ntee.html" target="_blank" rel="noopener noreferrer">NTEE Code Lookup</a> page.
                        </p>
                    </NteeDropdown>

                    <div className="mb-4">
                        <label className="block font-bold mb-1 text-sm sm:text-base">NTEE Code (Major)</label>                        
                        <p className="text-xs sm:text-sm mb-2 text-left italic text-gray-500">
                            The NTEE code (major) is the first letter of the NTEE code (full).
                        </p>
                        <p className="mb-2 text-left text-sm sm:text-base">{formValues.nteeFull ? `${formValues.nteeFull[0]} - ${getNteeMajorDescription(formValues.nteeFull[0])}` : 'No NTEE code selected'}</p>
                    </div>
                    
                    <div className="mb-4">
                        <label className="block font-bold mb-2 text-sm sm:text-base">Primary State Served <span className="text-red-500">*</span></label>
                        <select 
                            className="w-full p-2 sm:p-3 border rounded text-sm sm:text-base" 
                            value={formValues.primaryState}
                            onChange={(e) => handleChange('primaryState', e.target.value)}
                            required
                        >
                            <StateOptions />
                        </select>
                    </div>
                    <div className="mb-4">
                        <label className="block font-bold mb-2 text-sm sm:text-base">Additional Details <span className="text-red-500">*</span></label>
                        <p className="text-xs sm:text-sm mb-2 text-left italic text-gray-500">
                            Please provide additional details about the populations served, geographic areas served, and any other relevant information.
                        </p>
                        <textarea 
                            className="w-full p-2 sm:p-3 border rounded text-sm sm:text-base" 
                            value={formValues.additionalDetails}
                            rows="3"
                            onChange={(e) => handleChange('additionalDetails', e.target.value)}
                            placeholder="e.g. 'We serve children in the state of California'"
                            required
                        />
                    </div>

                    <div className='flex flex-col sm:flex-row items-center gap-3 sm:space-x-4'>
                        <button 
                            className="mt-4 p-2 sm:p-3 bg-blue-500 text-white rounded text-sm sm:text-base w-full sm:w-auto" 
                            onClick={handleSubmit}
                        >
                            Update Program
                        </button>
                        <button 
                            className="mt-2 sm:mt-4 p-2 sm:p-3 bg-gray-500 text-white rounded text-sm sm:text-base w-full sm:w-auto" 
                            onClick={() => setShowManageProgramModal(false)}
                        >
                            Cancel
                        </button>
                    </div>
                    {currentProgram && (
                        <button 
                            className='text-xs mt-4 text-left italic text-red-500'
                            onClick={() => handleDeleteProgram(currentProgram.id)}
                        > Delete Program</button>
                    )}
                    <p className='text-xs mt-2 text-left italic text-gray-300'>{currentProgram ? currentProgram.id : ''}</p>

                </div>                        
            </div>
        </div>
    );
};

export default ManageProgramModal;